import { logout } from '@/redux/slices/userSlice';
import { LogOut, Settings, HelpCircle, ChevronUp, CreditCard, User, Building2, Moon, Sun } from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../../redux/store';
import { useDarkMode } from '../../hooks/useDarkMode';
import { userSelectOneObjectByQuery } from '../../redux/thunks/DBServiceThunks';
import { logoutThunk } from '@/redux/thunks/UserThunks';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrgSlug, buildAccountSettingsUrl, buildOrganizationSettingsUrl, buildHelpUrl } from '../../utils/urlHelpers';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  fetchSubscriptionDataIfNeededThunk
} from '../../redux/thunks/SubscriptionThunks';
import {
  selectSubscriptionLoading,
  selectActiveSubscription
} from '../../redux/selectors/SubscriptionSelectors';
import React from 'react';
import { getLanguageDirection } from '../../i18n/config';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Card } from '../ui/card';

const UserProfileFooter: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = getLanguageDirection(i18n.language) === 'rtl';
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const orgSlug = useOrgSlug();
  const { orgSlug: urlOrgSlug } = useParams<{ orgSlug: string }>();
  // Use Redux org slug, fallback to URL org slug
  const effectiveOrgSlug = orgSlug || urlOrgSlug;
  // Select only the currently authenticated user from the local DB state
  const user = useAppSelector(state => {
    const authUserId = state.UserReducer.user?._id;
    return authUserId
      ? userSelectOneObjectByQuery(state.DBUserReducer, { _id: authUserId })
      : null;
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isDark, toggle: toggleDark } = useDarkMode();

  const subscriptionLoading = useAppSelector(selectSubscriptionLoading);
  const activeSubscription = useAppSelector(selectActiveSubscription);

  useEffect(() => {
    dispatch(fetchSubscriptionDataIfNeededThunk());
  }, [dispatch]);

  const getSubscriptionDisplayInfo = () => {
    if (subscriptionLoading) {
      return { planName: t('common.subscription.loading'), status: t('common.subscription.loading') };
    }

    if (activeSubscription) {
      const item = activeSubscription.items?.data[0];
      let planName = t('common.subscription.defaultPlanName');

      if (item?.price?.product && typeof item.price.product === 'object') {
        planName = item.price.product.name || t('common.subscription.defaultPlanName', 'Subscription Plan');
      } else {
        planName = item?.price?.nickname ||
          item?.plan?.nickname ||
          activeSubscription.plan?.nickname ||
          t('common.subscription.defaultPlanName');
      }

      const status = activeSubscription.status === 'active' ? t('common.subscription.statusActive') :
        activeSubscription.status.charAt(0).toUpperCase() + activeSubscription.status.slice(1);
      return { planName, status };
    }

    return { planName: t('common.subscription.noPlan'), status: t('common.subscription.statusInactive') };
  };

  const subscriptionInfo = getSubscriptionDisplayInfo();

  const getUserInitials = () => {
    if (!user?.displayName) return null;
    return user.displayName
      .split(' ')
      .map(name => name[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div
      dir={getLanguageDirection(i18n.language)}
      className="relative px-4 text-start"
    >
      {/* Backdrop */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-10"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Expanded Menu */}
      {isMenuOpen && (
        <Card className="absolute bottom-full left-2 right-2 mb-2 z-20 animate-fade-in overflow-hidden shadow-lg">
          {/* Profile Header */}
          <div className="p-4 bg-muted/30 border-b">
            <div className="flex items-center gap-3">
              <Avatar className="w-10 h-10">
                <AvatarImage src={user?.profilePicture} alt={t('common.labels.profile')} />
                <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
                  {getUserInitials() || <User size={20} />}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">
                  {user?.displayName || t('common.user.defaultName')}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {user?.email || t('common.user.noEmail')}
                </p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            <Button
              variant="ghost"
              onClick={() => { navigate(buildAccountSettingsUrl(effectiveOrgSlug)); setIsMenuOpen(false); }}
              className="w-full justify-start gap-3 px-4 h-auto py-3"
            >
              <Settings size={16} className="text-muted-foreground" />
              <span className="text-sm">{t('common.profile.accountSettings')}</span>
            </Button>

            <Button
              variant="ghost"
              onClick={() => { navigate(buildOrganizationSettingsUrl(effectiveOrgSlug)); setIsMenuOpen(false); }}
              className="w-full justify-start gap-3 px-4 h-auto py-3"
            >
              <Building2 size={16} className="text-muted-foreground" />
              <span className="text-sm">{t('common.profile.organizationSettings')}</span>
            </Button>

            {/* Dev Admin Button - Only in development and for system admins */}
            {import.meta.env.DEV && user?.role === 'admin' && (
              <Button
                variant="ghost"
                onClick={() => { navigate('/dev-admin'); setIsMenuOpen(false); }}
                className="w-full justify-start gap-3 px-4 h-auto py-3 text-amber-600 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950"
              >
                <Settings size={16} />
                <span className="text-sm font-semibold">{t('common.devAdmin.menuTitle')}</span>
              </Button>
            )}

            <Button
              variant="ghost"
              onClick={() => { navigate(buildAccountSettingsUrl(effectiveOrgSlug)); setIsMenuOpen(false); }}
              className={`w-full justify-start gap-3 px-4 h-auto py-3 ${activeSubscription ? 'bg-gold/5 border-l-4 border-l-gold' : 'bg-muted/50 border-l-4 border-l-muted-foreground'}`}
            >
              <CreditCard size={16} className={activeSubscription ? 'text-gold' : 'text-muted-foreground'} />
              <div className="flex-1 text-left">
                <p className={`text-sm font-semibold ${activeSubscription ? 'text-gold' : 'text-muted-foreground'}`}>
                  {t('common.profile.manageSubscription')}
                </p>
                <p className={`text-xs ${activeSubscription ? 'text-gold' : 'text-muted-foreground'}`}>
                  {subscriptionInfo.planName} - {subscriptionInfo.status}
                </p>
              </div>
            </Button>

            <Button
              variant="ghost"
              onClick={toggleDark}
              className="w-full justify-start gap-3 px-4 h-auto py-3"
            >
              {isDark
                ? <Sun size={16} className="text-muted-foreground" />
                : <Moon size={16} className="text-muted-foreground" />
              }
              <span className="text-sm">
                {isDark ? t('common.profile.lightMode') : t('common.profile.darkMode')}
              </span>
            </Button>

            <Separator className="my-2" />

            <Button
              variant="ghost"
              onClick={() => { navigate(buildHelpUrl(effectiveOrgSlug)); setIsMenuOpen(false); }}
              className="w-full justify-start gap-3 px-4 h-auto py-3"
            >
              <HelpCircle size={16} className="text-muted-foreground" />
              <span className="text-sm">{t('common.profile.helpSupport')}</span>
            </Button>

            <Button
              variant="ghost"
              onClick={() => { dispatch(logoutThunk() as any); setIsMenuOpen(false); }}
              className="w-full justify-start gap-3 px-4 h-auto py-3 text-destructive hover:text-destructive hover:bg-destructive/5"
            >
              <LogOut size={16} />
              <span className="text-sm">{t('common.profile.signOut')}</span>
            </Button>
          </div>
        </Card>
      )}

      {/* Collapsed Profile Button */}
      <div className="border-t border-border bg-background/50 p-4">
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="w-full flex items-center justify-between p-3 bg-card rounded-xl border border-border transition-smooth hover:bg-accent/50 hover:shadow-sm"
        >
          <div className="flex items-center gap-3">
            <Avatar className="w-8 h-8">
              <AvatarImage src={user?.profilePicture} alt={t('common.labels.profile')} />
              <AvatarFallback className="bg-gold/10 text-gold font-semibold text-xs">
                {getUserInitials() || <User size={16} />}
              </AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="text-sm font-semibold">{user?.displayName || t('common.user.defaultName')}</p>
              <p className="text-xs text-muted-foreground">{user?.email || t('common.user.noEmail')}</p>
            </div>
          </div>
          <ChevronUp
            size={16}
            className={`text-muted-foreground transition-transform ${isMenuOpen ? 'rotate-0' : 'rotate-180'}`}
          />
        </button>
      </div>
    </div>
  );
};

export default UserProfileFooter;
